import { Button, IconButton, TmdxSwitch } from '@ds';
import { MdOutlineModeEditOutline, MdOutlineOutbond } from 'react-icons/md';
import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { KidneyOrgan, PancreasOrgan } from '@icons';
import { AuthContext } from '@lp/core/auth';
import TabSection from '@features/components/CaseOverview/components/TabSection/TabSection';
import { Case, CaseStatus, ClinicalItemType, OrganType, ServiceType, SupplementalOrganType } from '@/api/apis/case';
import { useUpdateSupplementalOrganMutation } from '@/queries/case';
import { SectionMiniCard } from '../components/SectionMiniCard';
import { CaseDetailsPermissions } from '@/permissions';
import { getClinicalRecordByType, yesNo } from '@/utils/helpers';

type TxCenterProps = {
  caseData: Case;
};

const getValueStyles = (condition: boolean) => `${condition ? 'text-success-30' : 'text-base-30'} uppercase text-xl-m`;

export const SupplementalOrgans = ({ caseData }: TxCenterProps) => {
  const [editMode, setEditMode] = useState(false);
  const { clinicalData: clinicalInfo, caseId, serviceType, organType } = caseData;
  const { checkPermission } = useContext(AuthContext);
  const hasEditPermission = checkPermission(CaseDetailsPermissions.OrgansRecoveredUpdate);
  const hasEditDAPermission = checkPermission(CaseDetailsPermissions.OrgansRecoveredDAUpdate);
  const closedStatus = caseData.status === CaseStatus.Closed;
  const isDirectAcquisition = serviceType === ServiceType.DirectAcquisition;
  const hasEditOrgansPermission = hasEditPermission || (isDirectAcquisition && hasEditDAPermission);

  const supplementalOrgansData = getClinicalRecordByType(clinicalInfo, ClinicalItemType.SupplementalOrgan);

  const [kidneySelected, setKidneySelected] = useState(
    () =>
      supplementalOrgansData?.items?.find((subItem) => subItem.organ === SupplementalOrganType.Kidney)?.yesNo ?? false,
  );

  const [pancreasSelected, setPancreasSelected] = useState(
    () =>
      supplementalOrgansData?.items?.find((subItem) => subItem.organ === SupplementalOrganType.Pancreas)?.yesNo ??
      false,
  );

  const { mutateAsync } = useUpdateSupplementalOrganMutation();

  const handleSave = async () => {
    try {
      await mutateAsync({
        caseId,
        kidney: organType === OrganType.Kidney ? undefined : kidneySelected,
        pancreas: pancreasSelected,
        prevRecord: supplementalOrgansData,
      });
      toast.success('Supplemental organs updated successfully.');
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to update supplemental organs.');
    }
  };

  const getEditBtn = () => {
    if (hasEditOrgansPermission && !closedStatus) {
      return editMode ? (
        <Button size="sm" variant="light" onClick={handleSave}>
          Save
        </Button>
      ) : (
        <IconButton
          size="sm"
          variant="light"
          startContent={<MdOutlineModeEditOutline />}
          onClick={() => setEditMode(!editMode)}
        />
      );
    }
    return undefined;
  };

  return (
    <TabSection title="">
      <SectionMiniCard title="Supplemental Organs" icon={<MdOutlineOutbond size={24} />} rightElement={getEditBtn()}>
        <div className="flex flex-row gap-3">
          {organType !== OrganType.Kidney && (
            <div className="flex flex-col flex-1 h-[86px] bg-base-90 rounded-xl p-3 justify-end relative">
              <KidneyOrgan className="absolute right-1 top-1" />
              <span className="text-base-10 text-xs">KIDNEY</span>
              {editMode ? (
                <TmdxSwitch
                  color="default"
                  size="sm"
                  isSelected={kidneySelected}
                  onValueChange={(newValue) => setKidneySelected(newValue)}
                />
              ) : (
                <span className={getValueStyles(!!supplementalOrgansData && kidneySelected)}>
                  {supplementalOrgansData ? yesNo(kidneySelected) : '--:--'}
                </span>
              )}
            </div>
          )}
          <div className="flex flex-col flex-1 h-[86px] bg-base-90 rounded-xl p-3 justify-end relative">
            <PancreasOrgan className="absolute right-1 top-1" />
            <span className="text-base-10 text-xs">PANCREAS</span>
            {editMode ? (
              <TmdxSwitch
                color="default"
                size="sm"
                isSelected={pancreasSelected}
                onValueChange={(newValue) => setPancreasSelected(newValue)}
              />
            ) : (
              <span className={getValueStyles(!!supplementalOrgansData && pancreasSelected)}>
                {supplementalOrgansData ? yesNo(pancreasSelected) : '--:--'}
              </span>
            )}
          </div>
        </div>
      </SectionMiniCard>
    </TabSection>
  );
};
